/**
 * LineBatch — anti-aliased polylines in a single draw call.
 *
 * Geometry is built on the CPU once (miter joins, per-vertex width, distance along the line);
 * the vertex shader extrudes it every frame from the current zoom, so the same buffers render
 * crisp lines at any zoom: widths are world units clamped to a pixel range, dashes can be in
 * world units (roads, rail ties) or css pixels (UI-ish rings), and lines can be offset sideways
 * in pixels (double territory borders). Used for rivers, roads, rails, borders, fronts, the hex
 * grid, operation arrows and selection rings.
 */
import { Buffer, BufferUsage, Geometry, Mesh, type Shader } from 'pixi.js';
import { packRgba } from '../palette';
import { destroyMesh, FRAG_HEADER, makeShader, VERT_HEADER } from './gl';

export interface LineStyle {
  /** Full width in world units. */
  width: number;
  color: number;
  alpha?: number;
  /** Clamp of the on-screen width in css px (applied to the line's reference width). */
  minPx?: number;
  maxPx?: number;
  /** Dash pattern (world units unless `dashPx`). */
  dash?: number;
  gap?: number;
  dashPx?: boolean;
  /**
   * Sideways offset in css px along the normal (-dy, dx): with y pointing down that is the
   * right-hand side of the drawing direction (+y for an eastward line).
   */
  offsetPx?: number;
  /** Start value of the distance-along coordinate (keeps dashes continuous across pieces). */
  dist0?: number;
}

export interface LineOptions {
  /** Per-point full width (world units), overrides style.width. */
  widths?: ArrayLike<number>;
  closed?: boolean;
  /** Width the px clamp is computed from (default: per-vertex width). Keeps tapers proportional. */
  refWidth?: number;
}

const FLOATS = 14;
const STRIDE = FLOATS * 4;
const MITER_LIMIT = 4;

const VERT = /* glsl */ `${VERT_HEADER}
in vec2 aPos;
in vec2 aNormal;
in float aDist;
in float aWidth;
in float aRef;
in vec2 aLimits;
in vec2 aDash;
in float aOffset;
in float aSide;
in vec4 aColor;
uniform float uZoom;
uniform float uRes;
out vec4 vColor;
out float vAcross;
out float vHalf;
out float vCov;
out float vDist;
out vec2 vDash;
void main() {
  float refPx = aRef * uZoom;
  float scale = refPx > 0.0 ? clamp(refPx, aLimits.x * uRes, aLimits.y * uRes) / refPx : 1.0;
  float wpx = aWidth * uZoom * scale;
  float drawW = max(wpx, 1.0);
  vCov = min(wpx, 1.0);
  vHalf = drawW * 0.5;
  float ext = vHalf + 1.0;
  vAcross = aSide * ext;
  vec2 world = aPos + aNormal * ((aOffset * uRes + aSide * ext) / uZoom);
  vDist = aDash.x < 0.0 ? aDist * uZoom / uRes : aDist;
  vDash = abs(aDash);
  vColor = aColor;
  gl_Position = toClip(world);
}
`;

const FRAG = /* glsl */ `${FRAG_HEADER}
in vec4 vColor;
in float vAcross;
in float vHalf;
in float vCov;
in float vDist;
in vec2 vDash;
uniform vec4 uColor;
void main() {
  float a = clamp(vHalf + 0.5 - abs(vAcross), 0.0, 1.0) * vCov;
  if (vDash.x > 0.0) {
    float period = vDash.x + vDash.y;
    float ph = mod(vDist, period);
    float fw = max(fwidth(vDist), 1e-5);
    float sd = ph < vDash.x ? min(ph, vDash.x - ph) : -min(ph - vDash.x, period - ph);
    float dashed = clamp(sd / fw + 0.5, 0.0, 1.0);
    a *= mix(vDash.x / period, dashed, smoothstep(2.0, 4.0, period / fw));
  }
  float alpha = vColor.a * a * uColor.a;
  finalColor = vec4(vColor.rgb * uColor.rgb * alpha, alpha);
}
`;

export class LineBatch {
  readonly mesh: Mesh<Geometry, Shader>;
  private readonly vbuf: Buffer;
  private readonly ibuf: Buffer;
  private readonly geometry: Geometry;
  private verts: Float32Array;
  private u32: Uint32Array;
  private idx: Uint32Array;
  private vCount = 0;
  private iCount = 0;
  private readonly shader: Shader;

  constructor(label: string, capacity = 1024) {
    this.verts = new Float32Array(capacity * FLOATS);
    this.u32 = new Uint32Array(this.verts.buffer);
    this.idx = new Uint32Array(capacity * 3);
    this.vbuf = new Buffer({ data: this.verts, usage: BufferUsage.VERTEX | BufferUsage.COPY_DST, label });
    this.ibuf = new Buffer({ data: this.idx, usage: BufferUsage.INDEX | BufferUsage.COPY_DST, label });
    const at = (format: 'float32' | 'float32x2' | 'unorm8x4', offset: number) => ({
      buffer: this.vbuf,
      format,
      stride: STRIDE,
      offset,
    });
    this.geometry = new Geometry({
      attributes: {
        aPos: at('float32x2', 0),
        aNormal: at('float32x2', 8),
        aDist: at('float32', 16),
        aWidth: at('float32', 20),
        aRef: at('float32', 24),
        aLimits: at('float32x2', 28),
        aDash: at('float32x2', 36),
        aOffset: at('float32', 44),
        aSide: at('float32', 48),
        aColor: at('unorm8x4', 52),
      },
      indexBuffer: this.ibuf,
    });
    this.shader = makeShader('friction-lines', VERT, FRAG, {
      lineUniforms: { uZoom: { value: 1, type: 'f32' }, uRes: { value: 1, type: 'f32' } },
    });
    this.mesh = new Mesh({ geometry: this.geometry, shader: this.shader });
    this.mesh.label = label;
    this.mesh.visible = false;
  }

  setZoom(zoomCss: number, resolution: number): void {
    const u = this.shader.resources.lineUniforms.uniforms;
    u.uZoom = zoomCss * resolution;
    u.uRes = resolution;
  }

  clear(): void {
    this.vCount = 0;
    this.iCount = 0;
  }

  get isEmpty(): boolean {
    return this.iCount === 0;
  }

  /** Append a polyline [x0, y0, x1, y1, …]. Returns the total length (world units). */
  add(pts: ArrayLike<number>, style: LineStyle, opts: LineOptions = {}): number {
    const closed = !!opts.closed;
    let n = pts.length >> 1;
    if (closed && n > 2 && pts[0] === pts[n * 2 - 2] && pts[1] === pts[n * 2 - 1]) n--;
    if (n < 2) return 0;
    // closed rings repeat the first point at the end so the dash coordinate runs all the way round
    const total = closed ? n + 1 : n;
    this.reserve(total * 2, (total - 1) * 6);
    const color = packRgba(style.color, style.alpha ?? 1);
    const minPx = style.minPx ?? 0;
    const maxPx = style.maxPx ?? 1e4;
    const dash = style.dash && style.dash > 0 ? (style.dashPx ? -style.dash : style.dash) : 0;
    const gap = style.gap ?? 0;
    const off = style.offsetPx ?? 0;
    const base = this.vCount;
    let dist = style.dist0 ?? 0;
    const px = (i: number) => pts[(i % n) * 2];
    const py = (i: number) => pts[(i % n) * 2 + 1];
    for (let i = 0; i < total; i++) {
      const x = px(i);
      const y = py(i);
      // neighbours (wrapping for closed rings)
      const hasPrev = closed || i > 0;
      const hasNext = closed || i < total - 1;
      const ip = (i - 1 + n) % n;
      const inx = (i + 1) % n;
      let nx = 0;
      let ny = 0;
      let mx = 0;
      let my = 0;
      if (hasPrev) {
        const dx = x - px(ip);
        const dy = y - py(ip);
        const l = Math.hypot(dx, dy) || 1;
        nx = -dy / l;
        ny = dx / l;
      }
      if (hasNext) {
        const dx = px(inx) - x;
        const dy = py(inx) - y;
        const l = Math.hypot(dx, dy) || 1;
        mx = -dy / l;
        my = dx / l;
      }
      if (!hasPrev) {
        nx = mx;
        ny = my;
      }
      if (!hasNext) {
        mx = nx;
        my = ny;
      }
      let ax = nx + mx;
      let ay = ny + my;
      const al = Math.hypot(ax, ay);
      if (al < 1e-6) {
        ax = nx;
        ay = ny;
      } else {
        ax /= al;
        ay /= al;
        const cos = ax * nx + ay * ny;
        const scale = Math.min(MITER_LIMIT, 1 / Math.max(cos, 1e-3));
        ax *= scale;
        ay *= scale;
      }
      if (i > 0) dist += Math.hypot(x - px(i - 1), y - py(i - 1));
      const w = opts.widths ? opts.widths[i % n] : style.width;
      const ref = opts.refWidth ?? w;
      for (let s = 0; s < 2; s++) {
        const o = (this.vCount + i * 2 + s) * FLOATS;
        const v = this.verts;
        v[o] = x;
        v[o + 1] = y;
        v[o + 2] = ax;
        v[o + 3] = ay;
        v[o + 4] = dist;
        v[o + 5] = w;
        v[o + 6] = ref;
        v[o + 7] = minPx;
        v[o + 8] = maxPx;
        v[o + 9] = dash;
        v[o + 10] = gap;
        v[o + 11] = off;
        v[o + 12] = s === 0 ? 1 : -1;
        this.u32[o + 13] = color;
      }
    }
    for (let i = 0; i < total - 1; i++) {
      const a = base + i * 2;
      const o = this.iCount + i * 6;
      this.idx[o] = a;
      this.idx[o + 1] = a + 1;
      this.idx[o + 2] = a + 2;
      this.idx[o + 3] = a + 1;
      this.idx[o + 4] = a + 3;
      this.idx[o + 5] = a + 2;
    }
    this.vCount += total * 2;
    this.iCount += (total - 1) * 6;
    return dist - (style.dist0 ?? 0);
  }

  /** Upload what was added since clear(). */
  commit(): void {
    if (this.vbuf.data !== this.verts) this.vbuf.data = this.verts;
    else this.vbuf.update(this.vCount * STRIDE);
    if (this.ibuf.data !== this.idx) this.ibuf.data = this.idx;
    else this.ibuf.update(this.iCount * 4);
    this.geometry.indexCount = this.iCount;
    this.mesh.visible = this.iCount > 0;
  }

  private reserve(verts: number, indices: number): void {
    const needV = (this.vCount + verts) * FLOATS;
    if (needV > this.verts.length) {
      const next = new Float32Array(Math.max(needV, this.verts.length * 2));
      next.set(this.verts.subarray(0, this.vCount * FLOATS));
      this.verts = next;
      this.u32 = new Uint32Array(next.buffer);
    }
    const needI = this.iCount + indices;
    if (needI > this.idx.length) {
      const next = new Uint32Array(Math.max(needI, this.idx.length * 2));
      next.set(this.idx.subarray(0, this.iCount));
      this.idx = next;
    }
  }

  destroy(): void {
    destroyMesh(this.mesh);
  }
}
